import React, { useState } from "react";
import EmojiPicker from "emoji-picker-react";
import VoiceRecorder from "./VoiceRecorder";
import ReplyPreview from "./ReplyPreview";

// =====================================================
// MESSAGE INPUT (composer)
// Text entry + emoji + attach + voice recording, plus the contextual banners
// for replying to and editing a message. The container owns the text value
// and all send/edit logic; this component turns user intent into callback
// calls. When recording, the whole row is replaced by the recorder UI.
// =====================================================

function MessageInput({
  text,
  onTextChange,
  onSend,
  canChat,
  uploading,
  fileRef,
  onFileChange,
  replyTarget,
  onCancelReply,
  editTarget,
  onCancelEdit,
  onSendVoice,
}) {
  const [showEmoji, setShowEmoji] = useState(false);
  const [recording, setRecording] = useState(false);

  const disabled = uploading || !canChat;
  const editing = Boolean(editTarget);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    } else if (e.key === "Escape" && editing) {
      onCancelEdit();
    }
  };

  return (
    <>
      {editing && (
        <div className="compose-banner edit-banner">
          <span>✏️ Editing message</span>
          <button onClick={onCancelEdit} aria-label="Cancel edit">
            ✕
          </button>
        </div>
      )}

      {!editing && replyTarget && (
        <ReplyPreview
          reply={{
            senderName: replyTarget.__replyName,
            text: replyTarget.text,
            fileType: replyTarget.fileType,
          }}
          onCancel={onCancelReply}
        />
      )}

      {recording ? (
        <div className="chat-input">
          <VoiceRecorder
            onSend={(blob, duration) => {
              setRecording(false);
              onSendVoice(blob, duration);
            }}
            onCancel={() => setRecording(false)}
          />
        </div>
      ) : (
        <div className="chat-input">
          <button
            className="emoji-btn"
            onClick={() => setShowEmoji((v) => !v)}
            disabled={disabled}
          >
            😀
          </button>

          <button
            className="attach-btn"
            onClick={() => fileRef.current?.click()}
            disabled={disabled}
          >
            {uploading ? "⏳" : "📎"}
          </button>

          <input
            ref={fileRef}
            type="file"
            style={{ display: "none" }}
            onChange={onFileChange}
            accept="image/*,video/*,audio/*,.pdf,.mp3,.doc,.docx"
            disabled={disabled}
          />

          <input
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              !canChat
                ? "Chat unavailable"
                : uploading
                ? "Uploading..."
                : editing
                ? "Edit your message..."
                : "Type message..."
            }
            disabled={disabled}
          />

          {/* Mic when there's nothing to send; Send button once typing/editing. */}
          {!editing && !text.trim() ? (
            <button
              className="mic-btn"
              onClick={() => {
                setShowEmoji(false);
                setRecording(true);
              }}
              disabled={disabled}
              title="Record voice message"
            >
              🎤
            </button>
          ) : (
            <button
              onClick={onSend}
              className="send-btn"
              disabled={disabled}
            >
              {editing ? "Save" : "Send"}
            </button>
          )}

          {showEmoji && canChat && (
            <div className="emoji-picker-wrap">
              <EmojiPicker
                onEmojiClick={(e) => onTextChange(text + e.emoji)}
                theme="dark"
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default MessageInput;
